import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AppDialog from './AppDialog';

/**
 * Promise-based confirmation dialog — a styled drop-in replacement for window.confirm.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title, message, confirmText, severity }))) return;
 *
 * Options (all optional except message):
 *   message      string | node  — body text / question (required)
 *   title        string         — dialog heading (default 'Please confirm')
 *   confirmText  string         — confirm button label (default 'Confirm')
 *   cancelText   string         — cancel button label (default 'Cancel')
 *   severity     'error'|'warning'|'info'  — colours the confirm button (default 'warning')
 */
const ConfirmContext = createContext(() => Promise.resolve(false));

export function useConfirm() {
  return useContext(ConfirmContext);
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { options } when open, null when closed
  const resolver = useRef(null);

  const confirm = useCallback((options) => {
    const opts = typeof options === 'string' ? { message: options } : (options || {});
    setState({ options: opts });
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const close = (result) => {
    if (resolver.current) {
      resolver.current(result);
      resolver.current = null;
    }
    setState(null);
  };

  const opts = state?.options || {};
  const severity = opts.severity === 'error' || opts.severity === 'info' ? opts.severity : 'warning';
  const confirmColor = severity === 'error' ? 'error' : severity === 'info' ? 'primary' : 'warning';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AppDialog open={!!state} onClose={() => close(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {severity !== 'info' && (
            <WarningAmberIcon color={severity === 'error' ? 'error' : 'warning'} />
          )}
          {opts.title || 'Please confirm'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">{opts.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => close(false)}>{opts.cancelText || 'Cancel'}</Button>
          <Button
            variant="contained"
            color={confirmColor}
            onClick={() => close(true)}
            autoFocus
          >
            {opts.confirmText || 'Confirm'}
          </Button>
        </DialogActions>
      </AppDialog>
    </ConfirmContext.Provider>
  );
}

export default ConfirmProvider;
